import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { Operation } from 'fast-json-patch';
import { Communication, Encounter, Extension } from 'fhir/r4b';
import { useSuccessQuery } from 'utils';
import {
  getPatchBinary,
  PROVIDER_NOTIFICATION_METHOD_URL,
  PROVIDER_NOTIFICATIONS_ENABLED_URL,
  PROVIDER_NOTIFICATIONS_SETTINGS_EXTENSION_URL,
  ProviderNotificationMethod,
} from 'utils';
import { useApiClients } from '../../hooks/useAppClients';
import useEvolveUser from '../../hooks/useEvolveUser';

export type ProviderNotification = {
  appointmentID: string;
  encounter?: Encounter;
  communication: Communication;
  isRead: boolean;
};

export interface GetProviderNotificationsResponse {
  notifications: ProviderNotification[];
  totalUnread: number;
  hasMore: boolean;
  nextToken?: string;
}

export const useGetProviderNotifications = (
  nextToken?: string,
  onSuccess?: (data: GetProviderNotificationsResponse | null) => void
): UseQueryResult<GetProviderNotificationsResponse, Error> => {
  const { oystehrZambda } = useApiClients();
  const user = useEvolveUser();
  const queryResult = useQuery({
    queryKey: ['provider-notifications', nextToken],

    queryFn: async (): Promise<GetProviderNotificationsResponse> => {
      // Call the new zambda endpoint instead of direct FHIR query
      const response = (await oystehrZambda?.zambda.execute({
        id: 'get-professional-notifications',
        limit: 10,
        nextToken,
      })) as any;

      console.log('get-professional-notifications response:', response);

      // Handle raw response vs chooseJson style response handling
      const rawNotifications = response?.output?.notifications || response?.notifications || [];
      const totalUnread = response?.output?.totalUnread ?? response?.totalUnread ?? 0;
      const hasMore = response?.output?.hasMore ?? response?.hasMore ?? false;
      const fetchedNextToken = response?.output?.nextToken ?? response?.nextToken;

      if (!rawNotifications || rawNotifications.length === 0) {
        return { notifications: [], totalUnread: 0, hasMore: false };
      }

      // Map the zambda response to ProviderNotification format
      const notifications = rawNotifications.map((notification: any) => {
        const appointmentID = notification.idAppointment || '';

        const providerNotification: ProviderNotification = {
          appointmentID,
          isRead: notification.isRead,
          communication: {
            resourceType: 'Communication',
            id: notification.id,
            status: notification.isRead ? 'completed' : 'in-progress',
            sent: notification.sentDate,
            payload: [{ contentString: notification.message }],
            category: [{ coding: [{ code: notification.type }] }],
          } as Communication,
        };
        return providerNotification;
      });

      return {
        notifications,
        totalUnread,
        hasMore,
        nextToken: fetchedNextToken,
      };
    },

    enabled: !!(oystehrZambda && user?.profile),
    refetchInterval: 15000, // Slightly more relaxed refetch
    refetchIntervalInBackground: true,
  });

  useSuccessQuery(queryResult.data, onSuccess);

  return queryResult;
};

interface UpdateProviderNotificationsParams {
  method: ProviderNotificationMethod;
  enabled: boolean;
}

export const useUpdateProviderNotificationSettingsMutation = (
  onSuccess: (params: UpdateProviderNotificationsParams) => void
): UseMutationResult<UpdateProviderNotificationsParams, Error, UpdateProviderNotificationsParams> => {
  const user = useEvolveUser();
  const { oystehr } = useApiClients();
  return useMutation({
    mutationKey: ['provider-notifications'],

    mutationFn: async ({ method, enabled }: UpdateProviderNotificationsParams) => {
      if (!user?.profileResource) throw new Error('User practitioner profile not defined');

      const notificationsExtIndex = (user.profileResource.extension || [])?.findIndex(
        (ext) => ext.url === PROVIDER_NOTIFICATIONS_SETTINGS_EXTENSION_URL
      );

      const newNotificationSettingsExtension: Extension[] = [
        {
          url: PROVIDER_NOTIFICATIONS_SETTINGS_EXTENSION_URL,
          extension: [
            {
              url: PROVIDER_NOTIFICATION_METHOD_URL,
              valueString: method,
            },
            {
              url: PROVIDER_NOTIFICATIONS_ENABLED_URL,
              valueBoolean: enabled,
            },
          ],
        },
      ];

      let patchOp: Operation;

      if (!user.profileResource.extension) {
        patchOp = {
          op: 'add',
          path: `/extension`,
          value: newNotificationSettingsExtension,
        };
      } else {
        patchOp = {
          op: notificationsExtIndex >= 0 ? 'replace' : 'add',
          path: `/extension/${notificationsExtIndex >= 0 ? notificationsExtIndex : '-'}`,
          value: newNotificationSettingsExtension[0],
        };
      }

      await oystehr?.fhir.patch({
        id: user.profileResource.id ?? '',
        resourceType: 'Practitioner',
        operations: [patchOp],
      });
      return { method, enabled };
    },

    onSuccess,
  });
};

export const useUpdateProviderNotificationsMutation = (
  onSuccess?: () => void
): UseMutationResult<void, Error, { ids: NonNullable<Communication['id']>[]; status: Communication['status'] }> => {
  const { oystehr, oystehrZambda } = useApiClients();

  return useMutation({
    mutationKey: ['provider-notifications'],

    mutationFn: async (params: { ids: NonNullable<Communication['id']>[]; status: Communication['status'] }) => {
      const { ids, status } = params;

      // If status is 'completed', use the clear-professional-notifications zambda
      if (status === 'completed') {
        const clearResponse = await oystehrZambda?.zambda.execute({
          id: 'clear-professional-notifications',
          notificationIds: ids,
        });
        console.log('clear-professional-notifications response:', clearResponse);
      } else {
        const patchOp: Operation = {
          op: 'replace',
          path: '/status',
          value: status,
        };
        await oystehr?.fhir.batch({
          requests: [
            ...ids.map((id) =>
              getPatchBinary({ resourceId: id, resourceType: 'Communication', patchOperations: [patchOp] })
            ),
          ],
        });
      }
    },

    onSuccess,
  });
};

export const useCreateTestNotificationMutation = (
  onSuccess?: () => void
): UseMutationResult<void, Error, { practitionerId?: string; patientId?: string; type?: string; message?: string }> => {
  const { oystehrZambda } = useApiClients();

  return useMutation({
    mutationFn: async (params) => {
      await oystehrZambda?.zambda.execute({
        id: 'create-test-notification',
        ...params,
      });
    },
    onSuccess,
  });
};

export const useMarkReadProfessionalNotificationMutation = (
  onSuccess?: () => void
): UseMutationResult<void, Error, { id: string }> => {
  const { oystehrZambda } = useApiClients();

  return useMutation({
    mutationKey: ['provider-notifications'],
    mutationFn: async ({ id }) => {
      await oystehrZambda?.zambda.execute({
        id: 'mark-read-professional-notification',
        notificationId: id,
      });
    },
    onSuccess,
  });
};
